import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Check, X, Camera, ArrowUpRight } from "lucide-react";
import { PROOF_EXAMPLES, PROOF_RULES } from "@/lib/taxonomy";

export default function ProofGuide() {
  return (
    <div data-testid="proof-guide-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <div className="label-eyebrow">Seller guide</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
            How to earn the <span style={{ color: "#16A34A" }}>verified</span> badge.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-neutral-600 max-w-2xl">
            Every listing that claims a real brand is screened by our AI before it goes live. It is looking for the
            details a counterfeiter cannot fake — and it will not guess. Give it these four photos and honest pieces
            pass first time.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/sell" className="btn-primary" data-testid="guide-sell-btn">
              <Camera size={14} /> Create a listing
            </Link>
            <Link to="/brands" className="btn-outline" data-testid="guide-brands-btn">
              Heritage brands <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5 hairline p-6 h-fit" style={{ borderColor: "#16A34A" }}>
          <div className="flex items-center gap-2">
            <ShieldCheck size={15} style={{ color: "#16A34A" }} />
            <span className="label-eyebrow" style={{ color: "#16A34A" }}>What the badge does for you</span>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-neutral-600">
            <li>· Shows a green “Verified authentic” stamp on your card and product page</li>
            <li>· Appears in the buyer filter “Verified authentic only”</li>
            <li>· Counts toward your seller trust score and the Trusted Sellers row on the cover</li>
            <li>· Protects you: screened proof is your evidence if a buyer ever files a claim</li>
          </ul>
        </div>
      </div>

      <div className="mt-12 space-y-12">
        {PROOF_EXAMPLES.map((e, i) => (
          <section key={e.key} data-testid={`proof-example-${e.key}`}
            className={`grid lg:grid-cols-2 gap-8 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
            <div className="hairline overflow-hidden bg-neutral-50 lg:[direction:ltr]">
              <img src={e.img} alt={e.title} loading={i === 0 ? "eager" : "lazy"}
                className="w-full aspect-[4/3] object-cover" />
            </div>
            <div className="lg:[direction:ltr]">
              <div className="label-eyebrow">Photo {i + 1} of 4</div>
              <h2 className="font-display text-3xl tracking-tight mt-2">{e.title}</h2>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{e.why}</p>
              <div className="mt-4 hairline p-4">
                <div className="label-eyebrow">How to shoot it</div>
                <p className="mt-2 text-sm">{e.shoot}</p>
              </div>
            </div>
          </section>
        ))}
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-6">
        <div className="hairline p-6" style={{ borderColor: "#16A34A" }}>
          <div className="label-eyebrow" style={{ color: "#16A34A" }}>Do this</div>
          <ul className="mt-4 space-y-3 text-sm">
            {PROOF_RULES.do.map((r) => (
              <li key={r} className="flex gap-2">
                <Check size={15} className="mt-0.5 shrink-0" style={{ color: "#16A34A" }} />{r}
              </li>
            ))}
          </ul>
        </div>
        <div className="hairline p-6" style={{ borderColor: "#EA580C" }}>
          <div className="label-eyebrow" style={{ color: "#EA580C" }}>Never this</div>
          <ul className="mt-4 space-y-3 text-sm">
            {PROOF_RULES.dont.map((r) => (
              <li key={r} className="flex gap-2">
                <X size={15} className="mt-0.5 shrink-0" style={{ color: "#EA580C" }} />{r}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-12 hairline p-8 text-center">
        <div className="label-eyebrow">Didn't pass?</div>
        <p className="mt-3 text-sm text-neutral-600 max-w-2xl mx-auto">
          An “unverified” badge is not an accusation — it means the proof was not readable. Add better photos from your
          dashboard and re-submit (up to three attempts per listing). If you cannot photograph the brand markers, list
          the piece as <span className="text-black">Unbranded / Inspired by</span> and it goes live straight away.
        </p>
        <Link to="/dashboard" className="btn-outline mt-6 inline-flex" data-testid="guide-dashboard-btn">
          Go to your dashboard
        </Link>
      </div>
    </div>
  );
}
